import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { getCoachesAvailabilitiesQuerySchema, type GetCoachesAvailabilitiesQuery } from './coach.js';

const coachPoolFiltersSchema = getCoachesAvailabilitiesQuerySchema.pick({
  query: true,
  statuses: true,
  subStatuses: true,
  overallGrades: true,
  classEligibilities: true,
  backgroundCheckStatuses: true,
  backgroundCheckStatusesLogicalOperator: true,
  googleMeetTechCheck: true,
  hasAvailability: true,
  timeRanges: true,
  untakenTeachersOnly: true,
  coachAvailabilityType: true,
  states: true,
});
export type CoachPoolFilters = z.infer<typeof coachPoolFiltersSchema>;

export const createCoachPoolSchema = coachPoolFiltersSchema.extend({
  title: z.string().trim().min(1),
  description: z.string().optional(),
  isActive: z.boolean().optional(),
});
export type CreateCoachPool = z.infer<typeof createCoachPoolSchema>;
export const setCoachPoolActiveSchema = z
  .object({
    isActive: z.boolean(),
  })
  .strict();
export type SetCoachPoolActive = z.infer<typeof setCoachPoolActiveSchema>;

export const coachPoolSchema = coachPoolFiltersSchema.required().extend({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  query: z.string().nullable(),
  backgroundCheckStatusesLogicalOperator: coachPoolFiltersSchema.shape.backgroundCheckStatusesLogicalOperator
    .unwrap()
    .nullable(),
  googleMeetTechCheck: z.boolean().nullable(),
  hasAvailability: z.boolean().nullable(),
  timeRanges: coachPoolFiltersSchema.shape.timeRanges.unwrap().nullable(),
  untakenTeachersOnly: z.boolean().nullable(),
  coachAvailabilityType: coachPoolFiltersSchema.shape.coachAvailabilityType.unwrap().nullable(),
  createdAt: z.instanceof(Temporal.Instant),
  updatedAt: z.instanceof(Temporal.Instant),
});
export type CoachPool = z.infer<typeof coachPoolSchema>;

/** Filters the availability table uses for a saved pool. Empty optional fields are omitted. */
export function availabilityQueryFromCoachPool(pool: CoachPool): GetCoachesAvailabilitiesQuery {
  return {
    query: pool.query ?? undefined,
    statuses: pool.statuses,
    subStatuses: pool.subStatuses,
    overallGrades: pool.overallGrades,
    classEligibilities: pool.classEligibilities,
    backgroundCheckStatuses: pool.backgroundCheckStatuses,
    backgroundCheckStatusesLogicalOperator: pool.backgroundCheckStatusesLogicalOperator ?? undefined,
    googleMeetTechCheck: pool.googleMeetTechCheck ?? undefined,
    hasAvailability: pool.hasAvailability ?? undefined,
    timeRanges: pool.timeRanges ?? undefined,
    untakenTeachersOnly: pool.untakenTeachersOnly ?? undefined,
    coachAvailabilityType: pool.coachAvailabilityType ?? undefined,
    states: pool.states,
  };
}

export const coachPoolWithMatchCountSchema = coachPoolSchema.extend({
  matchingTeachersCount: z.number().int().nonnegative(),
  /** Teachers who match this active pool and no other active pool. */
  returningToNormalPriorityCount: z.number().int().nonnegative(),
  /** Teachers who match this active pool and at least one other active pool. */
  remainingReservedCount: z.number().int().nonnegative(),
});
export type CoachPoolWithMatchCount = z.infer<typeof coachPoolWithMatchCountSchema>;

export const listCoachPoolsResultSchema = z.object({
  pools: z.array(coachPoolWithMatchCountSchema),
  distinctReservedTeacherCount: z.number().int().nonnegative(),
});
export type ListCoachPoolsResult = z.infer<typeof listCoachPoolsResultSchema>;
